import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Users,
  CreditCard,
  FileText,
  Send,
  CheckCircle2,
  Clock,
  AlertCircle,
  ArrowRight,
  TrendingUp,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatCard = ({ title, value, icon: Icon, description, color, href }) => (
  <Link to={href}>
    <Card className="card-hover cursor-pointer h-full" data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold tracking-tight mt-2 font-mono">{value}</p>
            {description && (
              <p className="text-xs text-muted-foreground mt-2">{description}</p>
            )}
          </div>
          <div className={`p-3 rounded-lg ${color}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  </Link>
);

const DashboardPage = ({ user, settings }) => {
  const [stats, setStats] = useState({
    totalClients: 0,
    inProgress: 0,
    paidFull: 0,
    partialPayment: 0,
    signedForms: 0,
    completed: 0,
  });
  const [recentClients, setRecentClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [
        clientsRes,
        inProgressRes,
        paidFullRes,
        partialRes,
        signedRes,
        completedRes,
      ] = await Promise.all([
        axios.get(`${API}/clients`, { withCredentials: true }),
        axios.get(`${API}/reports/in-progress`, { withCredentials: true }),
        axios.get(`${API}/reports/paid-full`, { withCredentials: true }),
        axios.get(`${API}/reports/partial-payment`, { withCredentials: true }),
        axios.get(`${API}/reports/signed-forms`, { withCredentials: true }),
        axios.get(`${API}/reports/completed`, { withCredentials: true }),
      ]);

      setStats({
        totalClients: clientsRes.data.length,
        inProgress: inProgressRes.data.length,
        paidFull: paidFullRes.data.length,
        partialPayment: partialRes.data.length,
        signedForms: signedRes.data.length,
        completed: completedRes.data.length,
      });

      // Get 5 most recent clients
      const sorted = [...clientsRes.data].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setRecentClients(sorted.slice(0, 5));
    } catch (error) {
      console.error("Dashboard error:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} settings={settings}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} settings={settings}>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Tableau de bord
            </h1>
            <p className="text-muted-foreground mt-1">
              Année d'imposition {settings?.tax_year || 2025}
            </p>
          </div>
          <Button asChild data-testid="add-client-button">
            <Link to="/clients/new">
              <Users className="h-4 w-4 mr-2" />
              Nouveau client
            </Link>
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard
            title="Total Clients"
            value={stats.totalClients}
            icon={Users}
            color="bg-primary/10 text-primary"
            href="/clients"
          />
          <StatCard
            title="En cours"
            value={stats.inProgress}
            icon={Clock}
            description="Sans paiement ni signature"
            color="bg-yellow-100 text-yellow-700"
            href="/reports"
          />
          <StatCard
            title="Payés en totalité"
            value={stats.paidFull}
            icon={CreditCard}
            color="bg-green-100 text-green-700"
            href="/payments"
          />
          <StatCard
            title="Paiement partiel"
            value={stats.partialPayment}
            icon={AlertCircle}
            color="bg-orange-100 text-orange-700"
            href="/payments"
          />
          <StatCard
            title="Formulaires signés"
            value={stats.signedForms}
            icon={FileText}
            color="bg-blue-100 text-blue-700"
            href="/forms"
          />
          <StatCard
            title="Terminés"
            value={stats.completed}
            icon={CheckCircle2}
            description="TED après paiement et signature"
            color="bg-emerald-100 text-emerald-700"
            href="/status"
          />
        </div>

        {/* Recent Clients */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-semibold">Clients récents</CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/clients" className="text-primary">
                Voir tout
                <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {recentClients.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                <p className="text-muted-foreground">Aucun client pour le moment</p>
                <Button asChild className="mt-4" variant="outline">
                  <Link to="/clients/new">Ajouter un client</Link>
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="recent-clients-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Nom</th>
                      <th>Ville</th>
                      <th>Situation</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentClients.map((client) => (
                      <tr key={client.client_id} data-testid={`client-row-${client.code_client}`}>
                        <td className="font-mono text-sm">{client.code_client}</td>
                        <td className="font-medium">
                          {client.prenom} {client.nom}
                        </td>
                        <td className="text-muted-foreground">{client.ville}</td>
                        <td>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                            {client.situation_familiale}
                          </span>
                        </td>
                        <td className="text-right">
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/clients/${client.client_id}/edit`}>
                              Voir
                            </Link>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="card-hover">
            <CardContent className="p-4">
              <Link to="/payments" className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100">
                  <CreditCard className="h-5 w-5 text-green-700" />
                </div>
                <div>
                  <p className="font-medium text-foreground">Suivi Paiements</p>
                  <p className="text-xs text-muted-foreground">Gérer les paiements</p>
                </div>
              </Link>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="p-4">
              <Link to="/forms" className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <FileText className="h-5 w-5 text-blue-700" />
                </div>
                <div>
                  <p className="font-medium text-foreground">Formulaires</p>
                  <p className="text-xs text-muted-foreground">Suivi des signatures</p>
                </div>
              </Link>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="p-4">
              <Link to="/ted" className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100">
                  <Send className="h-5 w-5 text-purple-700" />
                </div>
                <div>
                  <p className="font-medium text-foreground">Transmission TED</p>
                  <p className="text-xs text-muted-foreground">Envoyer les déclarations</p>
                </div>
              </Link>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="p-4">
              <Link to="/reports" className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-100">
                  <TrendingUp className="h-5 w-5 text-orange-700" />
                </div>
                <div>
                  <p className="font-medium text-foreground">Éditions</p>
                  <p className="text-xs text-muted-foreground">Rapports et statistiques</p>
                </div>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
